import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, useTheme } from '@mui/material';
import { Home, ArrowBack } from '@mui/icons-material';
import LogoSVG from './common/LogoSVG';

function NotFound() {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: theme.palette.primary.main,
          opacity: 0.05,
          top: '-10%',
          left: '-5%',
          animation: 'float 20s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
            '50%': { transform: 'translate(30px, -30px) scale(1.1)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: theme.palette.secondary.main,
          opacity: 0.05,
          bottom: '-15%',
          right: '-10%',
          animation: 'float 25s ease-in-out infinite reverse',
        }}
      />

      <Container maxWidth="md">
        <Box textAlign="center">
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <LogoSVG width={200} height={60} />
          </Box>

          {/* 404 Text with Animation */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '120px', sm: '180px', md: '220px' },
              fontWeight: 800,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.05em',
              lineHeight: 1,
              mb: 0,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.8 },
              },
            }}
          >
            404
          </Typography>

          {/* Error Message */}
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Oops! Page Not Found
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Looks like you've wandered into uncharted studio territory. 
            The page you're looking for doesn't exist or has been moved.
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={() => navigate('/dashboard')}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #0066CC 30%, #0080FF 90%)',
                boxShadow: '0 3px 5px 2px rgba(0, 102, 204, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0052A3 30%, #0066CC 90%)',
                  boxShadow: '0 4px 8px 3px rgba(0, 102, 204, .3)',
                },
              }}
            >
              Go to Dashboard
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.primary.main + '10',
                },
              }}
            >
              Go Back
            </Button>
          </Box>

          {/* Fun Studio Elements */}
          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 4 }}>
            <Box
              sx={{
                opacity: 0.3,
                animation: 'bounce 2s ease-in-out infinite',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-10px)' },
                },
              }}
            >
              🎵
            </Box>
            <Box
              sx={{
                opacity: 0.3,
                animation: 'bounce 2s ease-in-out infinite 0.3s',
              }}
            >
              📸
            </Box>
            <Box
              sx={{
                opacity: 0.3,
                animation: 'bounce 2s ease-in-out infinite 0.6s',
              }}
            >
              🎤
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default NotFound;
